import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import { conversionLabel, convertTo, EConversionSIToImperial } from '../../../../conversion';
import './RockSlope.scss';
import { IRockSlopeProps } from './IRockSlope';
import { selectProjectInfo, selectMacro1 } from '../../../../store/projectInfo/projectInfoSlice';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { ErrorMessage, Field, Form, useFormik, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { setRockSlope } from '../../../store/project/projectslice';
import { RootState } from '../../../../../reduxService';
import React, { useEffect } from 'react';
import { updateValidation } from '../../../../Macro1/store/project/resultsSlice';
import Dialog from '../../../../../components/Dialog/Dialog';
import { Table } from '../../../../../components/Table/Table';
import FieldNumber from '../../../../../components/FieldNumber/FieldNumber';


import imgRockDensity from '../../../../images/macro1/rockDensity.png';
import imgRockSlope from '../../../../images/macro1/rockslope.png';
import imgExternalForce from '../../../../images/macro1/externalForce.png';
import imgJRC from '../../../../images/macro1/jrc.png';


export interface IRockSlopeForm {
    inclination: number,
    slopeAnchorAngleLimitUnlocked: boolean,
    thicknessSurficialInstability: number,
    rockDensity: number,
    dipSetJoints: number,
    compressiveStrengthCriticalJoint: number,
    roughnessCoefficientCriticalJoint: number,
    horizontalSeismicCoefficient: number,
    verticalSeismicCoefficient: number,
    externalForce: number,
    angleForceSlope: number,
}

export const RockSlopeDefault: IRockSlopeForm = {
    inclination: 65,
    slopeAnchorAngleLimitUnlocked: false,
    thicknessSurficialInstability: 0.5,
    rockDensity: 27,
    dipSetJoints: 25,
    compressiveStrengthCriticalJoint: 10,
    roughnessCoefficientCriticalJoint: 5,
    horizontalSeismicCoefficient: 0,
    verticalSeismicCoefficient: 0,
    externalForce: 0,
    angleForceSlope: 90,
};


const RockSlope = (props: IRockSlopeProps) => {
    const { t } = useTranslation();
    const projectInfo = useAppSelector(selectProjectInfo);
    const macro1State = useAppSelector(selectMacro1);
    const [UnlockedAngleAlert, setUnlockedAngleAlert] = React.useState<boolean>(false);
    const [CurrentDialog, setCurrentDialog] = React.useState<string>("");
    const openDialog = (dialogCode: string) => {
        setCurrentDialog(dialogCode);
    }
    const closeDialog = () => {
        setCurrentDialog('');
    }

    const checkConversion = (value: number, conversion: EConversionSIToImperial) => {
        return projectInfo.data?.unit == 'Metric' ? value : convertTo(value, conversion);
    }

    const RockSlopeValidationSchema = Yup.object().shape({
        inclination: Yup.number().min(36).label(t('rock_slope_inclination', { unit_measure: '' }))
            .when('slopeAnchorAngleLimitUnlocked', ([slopeAnchorAngleLimitUnlocked], sch) => {
            if (!slopeAnchorAngleLimitUnlocked) {
                const errorMessage = t("yup_custom.rock_slope.inclination");
                return sch.max(89.99 - (Number(macro1State.project.data?.anchorBar?.inclination) ?? 0), errorMessage);
            }
            return sch.max(360);
        }),
        slopeAnchorAngleLimitUnlocked: Yup.boolean().label(t('rock_slope_unlock_relation_limit', { unit_measure: '' })).required(),
        thicknessSurficialInstability: Yup.number().min(checkConversion(0.05, EConversionSIToImperial.m_to_ft)).max(checkConversion(3, EConversionSIToImperial.m_to_ft)).label(t('rock_slope_thickness_surficial_instability', { unit_measure: '' })).required(),
        rockDensity: Yup.number().min(0.01).label(t('rock_slope_rock_density', { unit_measure: '' })).required(),
        dipSetJoints: Yup.number().when('inclination', ([inclination], sch) => {
            return sch.required().min(0).max(inclination - 1).label(t('rock_slope_dip_set_joints', { unit_measure: '' }));
        }),
        compressiveStrengthCriticalJoint: Yup.number().min(0.01).label(t('rock_slope_compressive_strength_critical_joint', { unit_measure: '' })).required(),
        roughnessCoefficientCriticalJoint: Yup.number().min(0.01).max(20).label(t('rock_slope_roughness_coefficient_critical_joint', { unit_measure: '' })).required(),
        horizontalSeismicCoefficient: Yup.number().min(0).label(t('rock_slope_horizontal_seismic_coefficient', { unit_measure: '' })).required(),
        verticalSeismicCoefficient: Yup.number().min(0).label(t('rock_slope_vertical_seismic_coefficient', { unit_measure: '' })).required(),
        externalForce: Yup.number().min(0).label(t('rock_slope_external_force', { unit_measure: '' })).required(),
        angleForceSlope: Yup.number().min(0).max(90).label(t('rock_slope_angle_force_slope', { unit_measure: '' })).required(),
    });

    const dispatch = useAppDispatch();

    const dispatchRockSlope = (payload: IRockSlopeForm) => {
        dispatch(setRockSlope(payload));
    }

    const getMeasurementUnit = (unit: string[]) => {
        return ' [' + unit[projectInfo.data?.unit == 'Metric' ? 0 : 1] + ']';
    }

    const formikProps = useFormik({
        initialValues: macro1State.project?.data?.rockSlope ?? RockSlopeDefault,
        validationSchema: RockSlopeValidationSchema,
        onSubmit: (values, { setSubmitting }) => {
            setSubmitting(false);
        },
    });

    useEffect(() => {
        dispatchRockSlope(formikProps.values);
    }, [formikProps.values]);

    useEffect(() => {
        if (formikProps.values.slopeAnchorAngleLimitUnlocked) {
            setUnlockedAngleAlert(true);
        }
    }, [formikProps.values.slopeAnchorAngleLimitUnlocked]);

    useEffect(() => {
        formikProps.validateField('inclination');
    }, [macro1State.project.data?.anchorBar?.inclination]);

    const currentResults = useAppSelector((state: RootState) => {
        return { ...state.Macro1.results.data };
    });
    useEffect(() => {
        if (currentResults.validation?.startValidation && !currentResults.validation?.rockSlope) {
            formikProps.submitForm();
            formikProps.validateForm().then((errors) => {
                if (currentResults.validation)
                    dispatch(updateValidation({
                        ...currentResults.validation,
                        rockSlope: true,
                        rockSlopeErrors: Object.keys(errors).length > 0,
                    }));
            });

        }
    }, [JSON.stringify(currentResults.validation)]);

    const jcsValues = [
        { rock: "Cementing conglomerate", jcs: "70 - 100" },
        { rock: "Cementing sandstone", jcs: "75 - 160" },
        { rock: "Siltiti", jcs: "50 - 180" },
        { rock: "Mudstone", jcs: "50 - 180" },
        { rock: "Sandstone", jcs: "2 - 150" },
        { rock: "Marl - marlstone", jcs: "25 - 90" },
        { rock: "Dolomite", jcs: "60 - 300" },
        { rock: "Chalk", jcs: "15 - 30" },
        { rock: "Granite", jcs: "95 - 230" },
        { rock: "Porphyry", jcs: "100 - 230" },
        { rock: "Dolerite", jcs: "220 - 320" },
        { rock: "Tuff", jcs: "3 - 100" },
        { rock: "Andesite", jcs: "75 - 300" },
        { rock: "Basalt", jcs: "100 - 350" },
        { rock: "Gneiss", jcs: "80 - 160" },
        { rock: "Marble", jcs: "60 - 230" },
        { rock: "Serpentine", jcs: "20 - 130" },
        { rock: "Phylite", jcs: "20 - 80" },
        { rock: "Quartz", jcs: "85 - 350" },
        { rock: "Clay schist", jcs: "30 - 80" },
    ]

    return (
        <Grid container style={{ alignItems: 'center' }} className='style-form'>
            <Dialog
                open={UnlockedAngleAlert}
                title={`${t('warnings.generic.title')}`}
                onClose={() => { setUnlockedAngleAlert(false) }}>
                <>
                    <div>
                        {t('warnings.macro1.rockSlope.angleLimitUnlocked')}
                    </div>
                </>
            </Dialog>
            <FormikProvider value={formikProps}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('rock_slope_section_face_data')}</label>
                    </Grid>
                    <Grid item xs={11}>
                        <div className="d-flex">
                            <label>{t('rock_slope_inclination', { unit_measure: getMeasurementUnit(conversionLabel.deg) })}</label>
                            <i onClick={() => { openDialog('rock_slope') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
                        </div>
                        <FieldNumber name="inclination" step="0.01" disabled={props.readOnly} />
                        <ErrorMessage name="inclination" component="div" className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className="symbol">{t("rock_slope_symbols.rock_slope_inclination")}</label>
                    </div>
                    <Grid item xs={12}>
                        <label>{t('rock_slope_unlock_relation_limit')}</label>
                        <Field type="checkbox" name="slopeAnchorAngleLimitUnlocked" disabled={props.readOnly} />
                        <ErrorMessage name="slopeAnchorAngleLimitUnlocked" component="div" className="text-danger" />
                    </Grid>
                    <Grid item xs={11}>
                        <div className="d-flex">
                            <label>{t('rock_slope_thickness_surficial_instability', { unit_measure: getMeasurementUnit(conversionLabel.m_ft) })}</label>
                            <i onClick={() => { openDialog('rock_slope') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
                        </div>
                        <FieldNumber name="thicknessSurficialInstability" step="0.01" disabled={props.readOnly} />
                        <ErrorMessage name="thicknessSurficialInstability" component="div" className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className="symbol">{t("rock_slope_symbols.rock_slope_thickness_surficial_instability")}</label>
                    </div>
                    <Grid item xs={11}>
                        <div className="d-flex">
                            <label>{t('rock_slope_rock_density', { unit_measure: getMeasurementUnit(conversionLabel.kNm3_lbfft3) })}</label>
                            <i onClick={() => { openDialog('rock_density') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
                        </div>
                        <FieldNumber name="rockDensity" step="0.01" disabled={props.readOnly} />
                        <ErrorMessage name="rockDensity" component="div" className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className="symbol">{t("rock_slope_symbols.rock_slope_rock_density")}</label>
                    </div>
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('rock_slope_most_critical_set_joints')}</label>
                    </Grid>
                    <Grid item xs={11}>
                        <div className="d-flex">
                            <label>{t('rock_slope_dip_set_joints', { unit_measure: getMeasurementUnit(conversionLabel.deg) })}</label>
                            <i onClick={() => { openDialog('rock_slope') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
                        </div>
                        <FieldNumber name="dipSetJoints" step="0.01" disabled={props.readOnly} />
                        <ErrorMessage name="dipSetJoints" component="div" className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className="symbol">{t("rock_slope_symbols.rock_slope_dip_set_joints")}</label>
                    </div>
                    <Grid item xs={11}>
                        <div className="d-flex">
                            <label>{t('rock_slope_compressive_strength_critical_joint', { unit_measure: getMeasurementUnit(conversionLabel.mpa_mpa) })}</label>
                            <i onClick={() => { openDialog('jcs') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
                        </div>
                        <FieldNumber name="compressiveStrengthCriticalJoint" step="0.01" disabled={props.readOnly} />
                        <ErrorMessage name="compressiveStrengthCriticalJoint" component="div" className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className="symbol">{t("rock_slope_symbols.rock_slope_compressive_strength_critical_joint")}</label>
                    </div>
                    <Grid item xs={11}>
                        <div className="d-flex">
                            <label>{t('rock_slope_roughness_coefficient_critical_joint', { unit_measure: '' })}</label>
                            <i onClick={() => { openDialog('jrc') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
                        </div>
                        <FieldNumber name="roughnessCoefficientCriticalJoint" step="0.01" disabled={props.readOnly} />
                        <ErrorMessage name="roughnessCoefficientCriticalJoint" component="div" className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className="symbol">{t("rock_slope_symbols.rock_slope_roughness_coefficient_critical_joint")}</label>
                    </div>
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('rock_slope_seismicity')}</label>
                    </Grid>
                    <Grid item xs={11}>
                        <label>{t('rock_slope_horizontal_seismic_coefficient', { unit_measure: '' })}</label>
                        <FieldNumber name="horizontalSeismicCoefficient" step="0.01" disabled={props.readOnly} />
                        <ErrorMessage name="horizontalSeismicCoefficient" component="div" className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className="symbol">{t("rock_slope_horizontal_seismic_coefficient_symbol")}</label>
                    </div>
                    <Grid item xs={11}>
                        <label>{t('rock_slope_vertical_seismic_coefficient', { unit_measure: '' })}</label>
                        <FieldNumber name="verticalSeismicCoefficient" step="0.01" disabled={props.readOnly} />
                        <ErrorMessage name="verticalSeismicCoefficient" component="div" className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className="symbol">{t("rock_slope_vertical_seismic_coefficient_symbol")}</label>
                    </div>
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('rock_slope_section_external_force')}</label>
                    </Grid>
                    <Grid item xs={11}>
                        <div className="d-flex">
                            <label>{t('rock_slope_external_force', { unit_measure: getMeasurementUnit(conversionLabel.kN_lbf) })}</label>
                            <i onClick={() => { openDialog('external_force') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
                        </div>
                        <FieldNumber name="externalForce" step="0.01" disabled={props.readOnly} />
                        <ErrorMessage name="externalForce" component="div" className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className="symbol">{t("rock_slope_symbols.rock_slope_external_force")}</label>
                    </div>
                    <Grid item xs={11}>
                        <label>{t('rock_slope_angle_force_slope', { unit_measure: getMeasurementUnit(conversionLabel.deg) })}</label>
                        <FieldNumber name="angleForceSlope" step="0.01" disabled={props.readOnly} />
                        <ErrorMessage name="angleForceSlope" component="div" className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className="symbol">{t("rock_slope_symbols.rock_slope_angle_force_slope")}</label>
                    </div>
                </Grid>
            </FormikProvider>
            <Dialog
                open={CurrentDialog == 'rock_slope'}
                title={`${t('macro1_info_dialogs_rock_slope')}`}
                onClose={closeDialog}>
                <div>
                    <img
                        width='100%'
                        height='100%'
                        srcSet={`${imgRockSlope}?fit=crop&auto=format&dpr=2 2x`}
                        src={`${imgRockSlope}?fit=crop&auto=format&dpr=2 2x`}
                        alt="Rock Slope"></img>
                </div>
            </Dialog>
            <Dialog
                open={CurrentDialog == 'external_force'}
                title={`${t('macro1_info_dialogs_external_force')}`}
                onClose={closeDialog}>
                <div>
                    <img
                        width='100%'
                        height='100%'
                        srcSet={`${imgExternalForce}?fit=crop&auto=format&dpr=2 2x`}
                        src={`${imgExternalForce}?fit=crop&auto=format&dpr=2 2x`}
                        alt="External Force"></img>
                </div>
            </Dialog>
            <Dialog
                open={CurrentDialog == 'rock_density'}
                title={`${t('macro1_info_dialogs_rock_density')}`}
                onClose={closeDialog}>
                <div>
                    <img
                        width='100%'
                        height='100%'
                        srcSet={`${imgRockDensity}?fit=crop&auto=format&dpr=2 2x`}
                        src={`${imgRockDensity}?fit=crop&auto=format&dpr=2 2x`}
                        alt="Rock Density"></img>
                </div>
            </Dialog>
            <Dialog
                open={CurrentDialog == 'jrc'}
                title={`${t('macro1_info_dialogs_jrc')}`}
                onClose={closeDialog}>
                <div>
                    <img
                        width='100%'
                        height='100%'
                        srcSet={`${imgJRC}?fit=crop&auto=format&dpr=2 2x`}
                        src={`${imgJRC}?fit=crop&auto=format&dpr=2 2x`}
                        alt="Joint Roughness Coefficient"></img>
                </div>
            </Dialog>
            <Dialog
                open={CurrentDialog == 'jcs'}
                title={`${t('macro1_info_dialogs_jcs')}`}
                onClose={closeDialog}
                className="jcs-dialog"
            >
                <Grid container>
                    <Grid item xs={6}>
                        <img
                            width='100%'
                            height='auto'
                            srcSet={`${imgRockSlope}?fit=contain&auto=format&dpr=2 2x`}
                            src={`${imgRockSlope}?fit=contain&auto=format&dpr=2 2x`}
                            alt="Compressive Strength"></img>
                    </Grid>
                    <Grid item xs={6}>
                        <Table<{ rock: string, jcs: string }> data={jcsValues} columns={[
                            {
                                key: 'rock',
                                title: 'Rock',
                            },
                            {
                                key: 'jcs',
                                title: 'Compressive Strength',
                            },
                        ]} />
                    </Grid>
                </Grid>
            </Dialog>
        </Grid >
    );
};

export default RockSlope;
